// Core
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

// Components
import { Button, FieldSelect, FieldText, Modal } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { openNotificationPopup, openSuccessAlert } from "state/application/applicationSlice";
import {
  WEBHOOK_EVENTS,
  useCreateWebhooksMutation,
  useDeleteWebhookMutation,
  useGetIntegrationServicesQuery,
  useTestWebhookMutation,
  useUpdateWebhookMutation,
} from "state/integrations/integrationsApiSlice";
import IntegrationServiceAuth from "./IntegrationServiceAuth";

/* Suggested target for offers coming back from GDBN when the integration is
   linked to a community: the community's external offers endpoint. */
const suggestedUrl = (communityId) =>
  communityId
    ? `${window.location.origin}/core/api/communities/${communityId}` +
      `/energy_management/offers/external/GDBN_BEFLEXIBLE`
    : "";

function WebhookRow({ webhook, integrationId, notifyError }) {
  const { t } = useTranslation(["settings"]);
  const dispatch = useDispatch();
  const [url, setUrl] = useState(webhook.url);

  const [updateWebhook, { isLoading: isSaving }] = useUpdateWebhookMutation();
  const [deleteWebhook, { isLoading: isDeleting }] = useDeleteWebhookMutation();
  const [testWebhook, { isLoading: isTesting }] = useTestWebhookMutation();

  const dirty = url !== webhook.url;

  const onSave = async () => {
    try {
      await updateWebhook({ integrationId, webhookId: webhook.id, url }).unwrap();
      dispatch(openSuccessAlert({ title: t("integrations.webhooks.updated_success") }));
    } catch (error) {
      notifyError(error);
    }
  };

  const onDelete = async () => {
    try {
      await deleteWebhook({ integrationId, webhookId: webhook.id }).unwrap();
      dispatch(openSuccessAlert({ title: t("integrations.webhooks.deleted_success") }));
    } catch (error) {
      notifyError(error);
    }
  };

  const onTest = async () => {
    try {
      const result = await testWebhook({ integrationId, url }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("integrations.webhooks.test_result", { status: result?.statusCode }),
        })
      );
    } catch (error) {
      notifyError(error);
    }
  };

  const busy = isSaving || isDeleting || isTesting;

  return (
    <tr>
      <td>
        <input
          className="rec-input"
          aria-label={t("integrations.webhooks.url")}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </td>
      <td className="whitespace-nowrap">{webhook.event}</td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {dirty && (
          <button
            type="button"
            className="rec-icon-btn !w-[34px] !h-[34px]"
            title={t("integrations.webhooks.save")}
            disabled={busy}
            onClick={onSave}
          >
            <Icon name="save" style={{ fontSize: 18 }} />
          </button>
        )}
        <button
          type="button"
          className="rec-icon-btn !w-[34px] !h-[34px]"
          title={t("integrations.webhooks.test")}
          disabled={busy}
          onClick={onTest}
        >
          <Icon name="send" style={{ fontSize: 18 }} />
        </button>
        <button
          type="button"
          className="rec-icon-btn !w-[34px] !h-[34px]"
          title={t("integrations.webhooks.delete")}
          disabled={busy}
          onClick={onDelete}
        >
          <Icon name="delete" style={{ fontSize: 18 }} />
        </button>
      </td>
    </tr>
  );
}

WebhookRow.propTypes = {
  webhook: PropTypes.shape({
    id: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    event: PropTypes.string.isRequired,
  }).isRequired,
  integrationId: PropTypes.string.isRequired,
  notifyError: PropTypes.func.isRequired,
};

function IntegrationWebhooksModal({ integration, onClose }) {
  const { t } = useTranslation(["settings"]);
  const dispatch = useDispatch();

  const {
    data: services = [],
    isLoading,
    error: servicesError,
  } = useGetIntegrationServicesQuery(integration.id);
  const [createWebhooks, { isLoading: isAdding }] = useCreateWebhooksMutation();

  const [serviceId, setServiceId] = useState("");
  const [newUrl, setNewUrl] = useState(suggestedUrl(integration.community_id));
  const [newEvent, setNewEvent] = useState(WEBHOOK_EVENTS[0]);

  const serviceOptions = useMemo(
    () => services.map((s) => ({ value: s.id, label: s.name || s.id })),
    [services]
  );
  const activeServiceId = serviceId || services[0]?.id || "";
  const activeService = services.find((s) => s.id === activeServiceId);
  const webhooks = activeService?.webhooks || [];

  const notifyError = (error) => {
    dispatch(
      openNotificationPopup({
        notification: error?.data?.detail || t("integrations.error_generic"),
        type: "error",
      })
    );
  };

  const onAdd = async () => {
    try {
      await createWebhooks({
        integrationId: integration.id,
        serviceId: activeServiceId,
        webhooks: [{ url: newUrl, event: newEvent }],
      }).unwrap();
      dispatch(openSuccessAlert({ title: t("integrations.webhooks.created_success") }));
      setNewUrl(suggestedUrl(integration.community_id));
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t("integrations.webhooks.title")}
      subtitle={t("integrations.webhooks.subtitle")}
      maxWidth={760}
      footer={
        <div className="flex items-center justify-end">
          <Button variant="ghost" onClick={onClose}>
            {t("integrations.webhooks.close")}
          </Button>
        </div>
      }
    >
      {isLoading && (
        <div className="flex justify-center py-8 text-ink-3">
          <span className="animate-spin inline-flex">
            <Icon name="progress_activity" style={{ fontSize: 28, color: "var(--accent)" }} />
          </span>
        </div>
      )}
      {!isLoading && servicesError && (
        <div
          className="flex items-center gap-2 text-[13px]"
          style={{
            padding: 14,
            background: "var(--surface-2)",
            borderRadius: "var(--radius-sm)",
            color: "var(--danger)",
          }}
        >
          <Icon name="error" style={{ fontSize: 18 }} />
          {servicesError?.data?.detail || t("integrations.webhooks.load_error")}
        </div>
      )}
      {!isLoading && !servicesError && (
        <>
          <FieldSelect
            label={t("integrations.webhooks.service")}
            value={activeServiceId}
            options={serviceOptions}
            onChange={setServiceId}
          />

          {activeServiceId && (
            <IntegrationServiceAuth
              integrationId={integration.id}
              serviceId={activeServiceId}
              service={activeService}
            />
          )}

          <div className="rec-tbl-wrap mt-[14px]">
            <table className="rec-tbl rec-simtbl">
              <thead>
                <tr>
                  <th>{t("integrations.webhooks.url")}</th>
                  <th style={{ width: 200 }}>{t("integrations.webhooks.event")}</th>
                  <th style={{ width: 120 }} aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {webhooks.map((webhook) => (
                  <WebhookRow
                    key={webhook.id}
                    webhook={webhook}
                    integrationId={integration.id}
                    notifyError={notifyError}
                  />
                ))}
                {webhooks.length === 0 && (
                  <tr>
                    <td
                      colSpan={3}
                      style={{
                        textAlign: "center",
                        padding: 24,
                        color: "var(--ink-3)",
                        fontSize: 13,
                      }}
                    >
                      {t("integrations.webhooks.empty")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="rec-divider" />
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px_auto] gap-[10px] items-end">
            <FieldText label={t("integrations.webhooks.url")} value={newUrl} onChange={setNewUrl} />
            <FieldSelect
              label={t("integrations.webhooks.event")}
              value={newEvent}
              options={WEBHOOK_EVENTS.map((e) => ({ value: e, label: e }))}
              onChange={setNewEvent}
            />
            <Button disabled={isAdding || !newUrl || !activeServiceId} onClick={onAdd}>
              <Icon name="add" />
              {t("integrations.webhooks.add")}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}

IntegrationWebhooksModal.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.string.isRequired,
    community_id: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default IntegrationWebhooksModal;
